import { UNIT_SOLD_TYPES, REDEEMED_TYPES } from "./constants";
import { CSSProperties } from "react";

// ============================================================================
// transactionCalculations
// ============================================================================

export const calculateTransactionTotals = (
  transactions: any[],
  folioNo: string,
  openingUnits: number = 0
) => {
  let closingUnitsTotal = 0;

  const totals = {
    refundCapital: 0,
    units: 0,
    grossAmount: 0,
    saleLoad: 0,
    redemptionLoad: 0,
    cgt: 0,
    netAmount: 0,
    load: 0,
    tax: 0,
  };

  const processedTransactions = transactions.map((txn, index) => {
    const parsedTransfree = txn?.transfrees && JSON.parse(txn.transfrees);
    let broker = "";

    // Determine transaction type and calculate closing units
    if (UNIT_SOLD_TYPES.includes(txn?.txnr_type)) {
      closingUnitsTotal =
        index === 0
          ? closingUnitsTotal + +txn?.units + openingUnits
          : closingUnitsTotal + +txn?.units;
    } else if (REDEEMED_TYPES.includes(txn?.txnr_type)) {
      closingUnitsTotal =
        index === 0
          ? closingUnitsTotal - +txn?.units - openingUnits
          : closingUnitsTotal - +txn?.units;
    } else if (
      txn?.txnr_type === "transferofunits" &&
      parsedTransfree?.some((fol) => fol?.folio_no === folioNo)
    ) {
      broker = "receiver";
      closingUnitsTotal =
        index === 0
          ? closingUnitsTotal + +txn?.units - openingUnits
          : closingUnitsTotal + +txn?.units;
    } else if (
      txn?.txnr_type === "transferofunits" &&
      !parsedTransfree?.some((fol) => fol?.folio_no === folioNo)
    ) {
      broker = "sender";
      closingUnitsTotal =
        index === 0
          ? closingUnitsTotal - +txn?.units - openingUnits
          : closingUnitsTotal - +txn?.units;
    }

    // Accumulate totals
    totals.refundCapital += txn?.refund_of_capital || 0;
    totals.units += txn?.units || 0;
    totals.grossAmount += txn?.gross_amount || 0;
    totals.saleLoad += txn?.sale_load || 0;
    totals.redemptionLoad += txn?.redemption_load || 0;
    totals.cgt += txn?.tax_cgt || 0;
    totals.netAmount += txn?.net_amount || 0;
    totals.load += (txn?.sale_load || 0) + (txn?.redemption_load || 0);
    totals.tax += (txn?.tax_cgt || 0) + (txn?.tax_on_dvidend || 0);

    return {
      ...txn,
      closingUnits: closingUnitsTotal,
      broker,
      load: (txn?.sale_load || 0) + (txn?.redemption_load || 0),
      tax: (txn?.tax_cgt || 0) + (txn?.tax_on_dvidend || 0),
    };
  });

  return { processedTransactions, totals };
};

// ============================================================================
// participantCalculations
// ============================================================================

export const processParticipantData = (allData: any[]) => {
  let forParticipant = allData?.map((ele) => {
    let allTransactions: any = [];

    ele?.data1?.forEach((ele1) => {
      let temp1 = ele1?.Record;
      let subFunds =
        temp1?.systematic_investment?.investment_blocks?.[0]?.subFunds;
      const totalOpeningBalance =
        temp1?.systematic_investment?.investment_blocks?.[0].total_holding || 0;

      if (subFunds && subFunds.length > 0) {
        subFunds.forEach((child) => {
          allTransactions.push({
            date: temp1.created_at,
            txn_id: temp1?.txn_id,
            txnr_type: temp1?.txnr_type,
            fund_code: child?.fund_code,
            nav: child?.nav,
            units: child?.units,
            amount: child?.amount || child?.gross_amount,
            closing_balance: 0,
            allocation_type: child?.allocation_type || "",
            total_holding: temp1?.current_holding,
            total_unit: temp1?.units,
            nav_parent: temp1?.nav,
            holding: child?.holding || 0,
            total_opening_balance: totalOpeningBalance || 0,
            Record: {
              net_amount: child?.closing_balance || 0,
            },
          });
        });
      }
    });

    const groupedByFund = allTransactions.reduce((acc, transaction) => {
      const fundCode = transaction.fund_code;
      if (!acc[fundCode]) {
        acc[fundCode] = [];
      }
      acc[fundCode].push(transaction);
      return acc;
    }, {});

    let newChild = Object.keys(groupedByFund).map((fundCode) => [
      {
        fund_code: fundCode,
        subFunds: groupedByFund[fundCode],
        heading: ele?.heading,
      },
    ]);

    return {
      heading: ele?.heading,
      opening_balance: ele?.opening_balance || 0,
      closing_balance: ele?.closing_balance || 0,
      total_units: ele?.total_units || 0,
      total_grossAmount: ele?.total_grossAmount || 0,
      total_netAmount: ele?.total_netAmount || 0,
      total_CGT: ele?.total_CGT || 0,
      total_redemptionLoad: ele?.total_redemptionLoad || 0,
      total_saleLoad: ele?.total_saleLoad || 0,
      total_refundCapital: ele?.total_refundCapital || 0,
      newChild,
    };
  });

  const tempArr = forParticipant[0]?.newChild?.flat(1);

  const UpdatedArr = tempArr?.map((ele: any) => {
    let updatedSubFunds: any = [];
    let totalUnits = 0;
    let totalAmount = 0;
    let OpeningBalance = 0;

    ele?.subFunds?.forEach((ele1, index) => {
      let closingBalance;
      totalUnits += +ele1?.units || 0;
      totalAmount += +ele1?.amount || 0;

      if (index === 0) {
        OpeningBalance = ele1?.holding || 0;
        closingBalance =
          (parseFloat(ele1?.holding) || 0) + (parseFloat(ele1.units) || 0);
      } else {
        if (
          ele1.txnr_type === "investment" ||
          (ele1.txnr_type === "changeofallocation" &&
            (ele1?.allocation_type === "" ||
              ele1?.allocation_type === "conversionin"))
        ) {
          closingBalance = (+ele1?.holding || 0) + (+ele1.units || 0);
        } else if (
          ele1.txnr_type === "withdrawal" ||
          (ele1.txnr_type === "changeofallocation" &&
            ele1?.allocation_type &&
            ele1?.allocation_type === "conversionout")
        ) {
          closingBalance = (+ele1?.holding || 0) - (+ele1.units || 0);
        }
      }

      updatedSubFunds.push({
        ...ele1,
        closing_balance: closingBalance,
      });
    });

    const totalClosingBalance =
      updatedSubFunds[updatedSubFunds.length - 1]?.closing_balance;

    return {
      ...ele,
      subFunds: updatedSubFunds,
      opening_balance: OpeningBalance || 0,
      closing_balance: totalClosingBalance || 0,
      total_units: totalUnits || 0,
      total_amount: totalAmount || 0,
    };
  });

  return UpdatedArr;
};

export const getStyles = (
  options: {
    width?: string;
    textAlign?: CSSProperties["textAlign"];
    padding?: string;
  } = {}
): CSSProperties => {
  const { width, textAlign = "left", padding = "6px 4px" } = options;

  return {
    width,
    textAlign,
    padding,
  };
};
